import React from "react";
import { Card as AntCard, Skeleton, Row, Col, Space, Divider } from "antd";

const OrderDetailSkeleton = ({ isDarkMode = false }) => {
  const cardStyle = {
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    marginBottom: 20,
    background: isDarkMode ? "#1f1f1f" : "#ffffff",
    borderColor: isDarkMode ? "#303030" : "#f0f0f0",
  };

  return (
    <div style={{ padding: "4px 0" }}>
      {/* 1. Header Card Skeleton */}
      <AntCard style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <Space size="middle">
            <Skeleton.Button active size="small" style={{ width: 80, borderRadius: 6 }} />
            <Skeleton.Input active size="default" style={{ width: 220, borderRadius: 6 }} />
          </Space>
          <Space size="small">
            <Skeleton.Button active size="small" style={{ width: 85, borderRadius: 4 }} />
            <Skeleton.Button active size="small" style={{ width: 75, borderRadius: 4 }} />
            <Skeleton.Button active size="small" style={{ width: 95, borderRadius: 4 }} />
          </Space>
        </div>

        <Divider style={{ margin: "14px 0 20px 0" }} />

        {/* 3 Columns Customer Info */}
        <Row gutter={[24, 16]}>
          <Col xs={24} md={8}>
            <Skeleton
              active
              avatar={{ shape: "circle", size: 40 }}
              paragraph={{ rows: 2, width: ["80%", "60%"] }}
              title={{ width: "50%" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Skeleton
              active
              avatar={{ shape: "circle", size: 40 }}
              paragraph={{ rows: 2, width: ["90%", "70%"] }}
              title={{ width: "60%" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Skeleton
              active
              avatar={{ shape: "circle", size: 40 }}
              paragraph={{ rows: 2, width: ["75%", "55%"] }}
              title={{ width: "55%" }}
            />
          </Col>
        </Row>
      </AntCard>

      {/* 2. Order Summary (80%) + Shipping & Tracking (20%) Row */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        {/* Order Summary 80% */}
        <div style={{ flex: "1 1 78%", minWidth: "300px" }}>
          <AntCard style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 18,
              }}
            >
              <Skeleton.Input active size="small" style={{ width: 160 }} />
              <Skeleton.Button active size="small" style={{ width: 60 }} />
            </div>

            <Row gutter={[24, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12}>
                <Skeleton
                  active
                  paragraph={{ rows: 3, width: ["100%", "90%", "80%"] }}
                  title={false}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Skeleton
                  active
                  paragraph={{ rows: 3, width: ["100%", "90%", "80%"] }}
                  title={false}
                />
              </Col>
            </Row>

            {/* Profit banner skeleton */}
            <div
              style={{
                padding: "16px",
                borderRadius: 8,
                background: isDarkMode ? "#141414" : "#f6ffed",
                border: isDarkMode ? "1px solid #303030" : "1px solid #b7eb8f",
                marginBottom: 16,
              }}
            >
              <Skeleton
                active
                paragraph={{ rows: 1, width: "60%" }}
                title={{ width: "35%" }}
              />
            </div>

            {/* Transactions skeleton */}
            <Skeleton active paragraph={{ rows: 2 }} title={{ width: "40%" }} />
          </AntCard>
        </div>

        {/* Shipping & Tracking 20% */}
        <div
          style={{
            flex: "0 0 260px",
            minWidth: "240px",
            maxWidth: "100%",
          }}
        >
          <AntCard style={cardStyle} bodyStyle={{ padding: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 18,
              }}
            >
              <Skeleton.Input active size="small" style={{ width: 140 }} />
              <Skeleton.Button active size="small" style={{ width: 50 }} />
            </div>

            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Skeleton.Input
                active
                size="default"
                style={{
                  width: "100%",
                  height: 38,
                  borderRadius: 6,
                  marginBottom: 14,
                }}
              />
              <Skeleton.Button
                active
                size="large"
                block
                style={{ height: 40, borderRadius: 6 }}
              />
            </div>
          </AntCard>
        </div>
      </div>

      {/* 3. Lower Tabs & Product Cards Skeleton */}
      <AntCard style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
        {/* Tab Headers Skeleton */}
        <div
          style={{
            display: "flex",
            gap: 18,
            marginBottom: 24,
            borderBottom: isDarkMode ? "1px solid #303030" : "1px solid #f0f0f0",
            paddingBottom: 12,
            overflowX: "auto",
          }}
        >
          <Skeleton.Button active size="small" style={{ width: 170, borderRadius: 4 }} />
          <Skeleton.Button active size="small" style={{ width: 160, borderRadius: 4 }} />
          <Skeleton.Button active size="small" style={{ width: 175, borderRadius: 4 }} />
          <Skeleton.Button active size="small" style={{ width: 165, borderRadius: 4 }} />
        </div>

        {/* 6 in a row product card skeletons matching the PC layout! */}
        <Row gutter={[12, 12]}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={4} key={item}>
              <div
                style={{
                  border: isDarkMode ? "1px solid #303030" : "1px solid #f0f0f0",
                  borderRadius: 8,
                  overflow: "hidden",
                  padding: 10,
                  background: isDarkMode ? "#141414" : "#ffffff",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: 130,
                    borderRadius: 6,
                    background: isDarkMode ? "#262626" : "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 10,
                  }}
                >
                  <Skeleton.Avatar active shape="square" size={70} />
                </div>
                <Skeleton
                  active
                  paragraph={{ rows: 2, width: ["100%", "70%"] }}
                  title={{ width: "85%" }}
                />
              </div>
            </Col>
          ))}
        </Row>
      </AntCard>
    </div>
  );
};

export default OrderDetailSkeleton;
